/**
 * Даты для полей <input type="datetime-local"> в часовом поясе Asia/Tashkent.
 *
 * Поле datetime-local не знает зону: оно показывает и возвращает «стену часов»
 * (`YYYY-MM-DDTHH:mm`). Раньше значение подставлялось как UTC, а обратно парсилось
 * как локальное время браузера, и каждое сохранение сдвигало дату на +5 ч.
 * Теперь и показ, и разбор идут в одном поясе — Ташкенте, независимо от пояса
 * компьютера админа. В Узбекистане нет перехода на летнее время, смещение всегда +05:00.
 */

export const TASHKENT_TZ = 'Asia/Tashkent';
export const TASHKENT_OFFSET = '+05:00';
const TASHKENT_OFFSET_MS = 5 * 60 * 60 * 1000;

const LOCAL_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** ISO-момент (или Date) → значение для datetime-local во времени Ташкента. Пусто/невалидно → ''. */
export function toTashkentInput(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  const ms = date.getTime();
  if (Number.isNaN(ms)) return '';
  // Сдвигаем момент на +5 ч и читаем его UTC-компоненты — это и есть ташкентские часы.
  return new Date(ms + TASHKENT_OFFSET_MS).toISOString().slice(0, 16);
}

/**
 * Значение datetime-local (время Ташкента) → ISO-момент в UTC (`…Z`).
 * Пусто → null. Невалидная строка → null.
 */
export function fromTashkentInput(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = LOCAL_INPUT_RE.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s ?? '00'}${TASHKENT_OFFSET}`);
  const ms = date.getTime();
  if (Number.isNaN(ms)) return null;
  // Отсекаем «31 февраля» и т.п.: Date молча переносит их на следующий месяц.
  if (toTashkentInput(date).slice(0, 10) !== `${y}-${mo}-${d}`) return null;
  return date.toISOString();
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Начало дня `YYYY-MM-DD` по Ташкенту → ISO-момент (для фильтра «с»). */
export function tashkentDayStartIso(date: string): string | null {
  if (!DATE_RE.test(date)) return null;
  return fromTashkentInput(`${date}T00:00`);
}

/** Конец дня `YYYY-MM-DD` по Ташкенту (включительно, …:59.999) → ISO-момент (для фильтра «по»). */
export function tashkentDayEndIso(date: string): string | null {
  const start = tashkentDayStartIso(date);
  if (!start) return null;
  return new Date(new Date(start).getTime() + 24 * 60 * 60 * 1000 - 1).toISOString();
}

/** Сегодняшняя дата `YYYY-MM-DD` по Ташкенту, независимо от пояса компьютера админа. */
export function tashkentToday(now: Date = new Date()): string {
  return toTashkentInput(now).slice(0, 10);
}

/**
 * Строка даты для шапки админки: «Пятница, 31 октября» (ru, время Ташкента).
 * Пояс фиксирован — не зависит от часов компьютера админа.
 */
export function formatHeaderDate(date: Date = new Date()): string {
  const s = new Intl.DateTimeFormat('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: TASHKENT_TZ,
  }).format(date);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Время «ЧЧ:ММ» по Ташкенту. */
export function formatTashkentTime(date: string | Date): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: TASHKENT_TZ }).format(d);
}

/** «дд.мм, ЧЧ:ММ» по Ташкенту (короткая метка в таблицах). */
export function formatTashkentShort(date: string | Date): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TASHKENT_TZ,
  }).format(d);
  return parts;
}
